// @ts-check
import { useEntryItems } from '@/core/composables/entries/useEntryItems'
import * as _ from 'lodash-es'
import { getField } from 'vuex-map-fields'

import {
  supplierType as assignmentSupplierType,
  entryCountry
} from '@/core/constants'

const { COMPANY, FINNISH_COMPANY, EU_COMPANY, OUTSIDE_EU_COMPANY } =
  assignmentSupplierType
const { FINLAND, EU, OTHER } = entryCountry

/**
 * Returns the final supplier type based on the country and supplier type of
 * the assignment.
 * @param {EntryModuleState} state
 * @returns {string}
 */
const finalSupplierType = ({ supplier: { type, country } }) => {
  if (type === COMPANY) {
    switch (country) {
      case FINLAND:
        return FINNISH_COMPANY
      case EU:
        return EU_COMPANY
      case OTHER:
      default:
        return OUTSIDE_EU_COMPANY
    }
  }
  return type
}

/** @type {EntryGetterTree} */
export const assignmentGetters = {
  getField,

  id: (state) => state.id,

  items: (state) => state.items,

  type: (state) => state.type,

  date: (state) => state.date,

  country: (state) =>
    state.type === 'expense' ? state.supplier.country : state.items[0].country,

  description: (state) => state.description,

  supplier: (state) => ({
    ...state.supplier,
    finalType: finalSupplierType(state)
  }),

  expenseZeroVatOnly: (state) => state.supplier?.type === 'person',

  files: (state) => state.files,

  paymentReductions: (state) => state.paymentReductions,

  hasPaymentReductions: (state) => state.hasPaymentReductions,

  requiresSupplierId: ({ supplier: { type, country } }) => {
    return !(type === 'person' || country === 'other')
  },

  shouldValidateId: (state) => finalSupplierType(state) === 'finnish_company',

  finalSupplierType,

  assignment: (state) =>
    _.pick(state, [
      'type',
      'date',
      'description',
      'items',
      'files',
      'paymentReductions',
      'hasPaymentReductions',
      'supplier'
    ]),

  itemsTotals: (state) => {
    const { itemsTotals } = useEntryItems(state.items)
    return itemsTotals.value
  },

  itemsTotalUnchecked: (state) =>
    state.items.reduce((acc, { price }) => acc + price, 0),

  isIncome: (state) => state.type === 'income',

  isExpense: (state) => state.type === 'expense',

  endpoint: (state) => `${state.type}s`,

  hasDuplicateAttachment: ({ files }) =>
    files.some((attachment) => attachment.is_duplicate)
}

export default assignmentGetters
